import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Menu, X, Phone, MapPin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [address, setAddress] = useState("");
  const navigate = useNavigate();

  const navLinks = [
    { href: "#how-it-works", label: "How It Works" },
    { href: "#why-us", label: "Why Choose Us" },
    { href: "#testimonials", label: "Testimonials" },
    { href: "#faq", label: "FAQ" },
  ];

  const scrollToSection = (href) => {
    setIsOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleAddressSubmit = (e) => {
    e.preventDefault();
    if (address.trim()) {
      navigate(`/get-offer?address=${encodeURIComponent(address)}`);
    } else {
      navigate('/get-offer');
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a 
            href="/" 
            className="flex items-center gap-2"
            data-testid="logo-link"
          >
            <div className="w-10 h-10 bg-[#D32F2F] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>JJ</span>
            </div>
            <span 
              className="hidden sm:block text-xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              buyshouses
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
                data-testid={`nav-${link.href.replace('#', '')}`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a 
              href="tel:626-526-1420" 
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              <span>626-526-1420</span>
            </a>
            <Button 
              className="bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-bold px-6 shadow-lg hover:shadow-xl transition-all"
              onClick={() => navigate('/get-offer')}
              data-testid="header-cta-btn"
            >
              Get Cash Offer
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[350px]">
              <nav className="flex flex-col gap-4 mt-8">
                {navLinks.map((link) => (
                  <button
                    key={link.href}
                    onClick={() => scrollToSection(link.href)}
                    className="text-left text-lg text-slate-600 hover:text-slate-900 font-medium py-2 border-b border-slate-100"
                    data-testid={`mobile-nav-${link.href.replace('#', '')}`}
                  >
                    {link.label}
                  </button>
                ))}
                <a 
                  href="tel:626-526-1420" 
                  className="flex items-center gap-2 text-lg text-slate-900 font-semibold py-4"
                  data-testid="mobile-phone"
                >
                  <Phone className="w-5 h-5 text-[#D32F2F]" />
                  <span>626-526-1420</span>
                </a>
                <Button 
                  className="bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-bold w-full py-6 text-lg"
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/get-offer');
                  }}
                  data-testid="mobile-cta-btn"
                >
                  Get Your Cash Offer
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
